// src/pages/RouteReportsPage.tsx
//
// Renders the rebound RDL reports (RouteSheet, RouteSheet-AllDrivers, and the
// virtual "LoadSheet" name — resolved per tenant to the Devash 3-section
// layout or the generic single-section sheet) against the agent's local
// route-snapshot. Replaces the PBI-based "Load / Route Sheets" flow.
//
// Flow:
//   1. Page loads -> fetch snapshot status + driver list
//   2. User clicks "Refresh Snapshot" -> POST /api/route-snapshot/run
//   3. User picks a driver, clicks a render button -> POST /api/report/render
//      -> binary PDF -> browser download
//
// All calls go DIRECT browser -> agent (apiV2.getAgentClient) so renders that
// take 30-60s don't hit Netlify Function timeouts.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, Truck, FileText, Download, CheckCircle2,
  AlertTriangle, Clock, Users, Send, X, Settings,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { adminApi } from '../services/adminApi';
import { apiV2, downloadFile, type RouteSnapshotResult, type RouteSnapshotStatus, type SendNowResult, type RouteContentMode } from '../services/apiV2';
import ReportScheduleManager from '../components/ReportScheduleManager';

interface RouteReportsPageProps {
  onBack?: () => void;
}

type RenderTarget = 'RouteSheet' | 'RouteSheet-AllDrivers' | 'LoadSheet';

// LoadSheet layout override. '' = tenant default (route_reports.loadsheet_mode
// in System Admin). 'Groups' = the Devash 3-section layout; the rest select
// the generic single-section sheet filtered to that group.
const LOAD_GROUPS = [
  { value: '', label: 'Tenant default' },
  { value: 'All', label: 'All items' },
  { value: 'Groups', label: 'Crates / Pallets / Orders (3 sections)' },
  { value: 'Crates', label: 'Crates only' },
  { value: 'Pallets', label: 'Pallets only' },
  { value: 'Orders', label: 'Pallet orders only' },
] as const;

interface RecentRender {
  target: RenderTarget;
  driver?: string;
  filename: string;
  sizeKB: number;
  at: Date;
}

export default function RouteReportsPage({ onBack }: RouteReportsPageProps) {
  const { branding, activeTenant, claims } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? undefined;
  // Single daily batch mode (route_reports.daily_batch): the tenant's ACTUAL flag row,
  // not canSee (which is always true for sysadmins). On = one send/day that picks every
  // order incl. 003, so the modal offers just that send.
  const featuresQ = useQuery({
    queryKey: ['route-reports', 'features', slug],
    queryFn: () => adminApi.getAllFeatures(slug),
    enabled: !!slug,
    refetchOnWindowFocus: false, retry: 0, staleTime: 60_000,
  });
  const dailyBatch = (featuresQ.data ?? []).some((f) => f.featureKey === 'route_reports.daily_batch' && f.isEnabled);

  const [status, setStatus] = useState<RouteSnapshotStatus | null>(null);
  const [drivers, setDrivers] = useState<string[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [loadGroup, setLoadGroup] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [rendering, setRendering] = useState<RenderTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recents, setRecents] = useState<RecentRender[]>([]);

  // Send Now modal state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendNumber, setSendNumber] = useState<1 | 2>(1);
  // Load-sheet content: 'all' (original, every order) or 'unpicked' (batch pre-pick).
  const [sendMode, setSendMode] = useState<RouteContentMode>('all');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendNowResult | null>(null);

  // Manage (ReportScheduleManager full-page takeover) mode
  const [manageOpen, setManageOpen] = useState(false);

  // --- Initial load ----------------------------------------------------------

  const loadStatusAndDrivers = async () => {
    setError(null);
    try {
      const [s, d] = await Promise.all([
        apiV2.getRouteSnapshotStatus(),
        apiV2.listRouteDrivers().catch(() => [] as string[]),
      ]);
      setStatus(s);
      setDrivers(d);
      if (d.length > 0 && !selectedDriver) setSelectedDriver(d[0]);
    } catch (e) {
      setError(formatError(e));
    }
  };

  useEffect(() => {
    loadStatusAndDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Actions ---------------------------------------------------------------

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await apiV2.runRouteSnapshot();
      await loadStatusAndDrivers();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setRefreshing(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    setSendResult(null);
    setError(null);
    try {
      // Daily mode = round 2 (all drivers incl. 003) as a batch, emailed like the Second Send.
      const result = dailyBatch ? await apiV2.sendNow(2, 'unpicked') : await apiV2.sendNow(sendNumber, sendMode);
      setSendResult(result);
      // Refresh status so the user can see the latest snapshot info
      await loadStatusAndDrivers();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSending(false);
    }
  };

  const handleRender = async (target: RenderTarget) => {
    setRendering(target);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (target === 'RouteSheet') {
        if (!selectedDriver) {
          setError('Pick a driver before rendering the per-driver RouteSheet.');
          setRendering(null);
          return;
        }
        params.Query4Driver = selectedDriver;
      }
      if (target === 'LoadSheet' && loadGroup) {
        params.LoadGroup = loadGroup;
      }
      let blob: Blob;
      try {
        blob = await apiV2.renderReport(target, params);
      } catch (e) {
        // Agents older than v3.7.2 don't know the virtual "LoadSheet" name —
        // fall back to the original Devash RDL so the button keeps working
        // until the tenant's agent is updated.
        if (target === 'LoadSheet' && String(e).toLowerCase().includes('not found')) {
          blob = await apiV2.renderReport('RouteItems-All', {});
        } else {
          throw e;
        }
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const driverTag = target === 'RouteSheet' ? `-${selectedDriver}` : '';
      const filename = `${target}${driverTag}-${stamp}.pdf`;
      downloadFile(blob, filename);
      setRecents((prev) => [
        {
          target,
          driver: target === 'RouteSheet' ? selectedDriver : undefined,
          filename,
          sizeKB: Math.round(blob.size / 1024),
          at: new Date(),
        },
        ...prev,
      ].slice(0, 10));
    } catch (e) {
      setError(formatError(e));
    } finally {
      setRendering(null);
    }
  };

  // --- Derived display values ------------------------------------------------

  const isEnabled = status?.enabled ?? false;
  const lastRun: RouteSnapshotResult | null = status?.lastRun ?? null;
  const lastRunAt = lastRun ? new Date(lastRun.ranAt) : null;
  const nextDeliveryDate = lastRun ? new Date(lastRun.nextDeliveryDate).toLocaleDateString() : '—';

  // --- Render ----------------------------------------------------------------

  // Manage mode: full-page takeover. Reuses the v1 admin UI (Schedules /
  // Recipients / Drivers / History) against the SAME SharePoint lists the
  // new orchestrator reads from, so once v1 is disabled this is the only
  // place to manage them.
  if (manageOpen) {
    return <ReportScheduleManager onBack={() => setManageOpen(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 min-h-16 py-2 md:py-0 md:h-16 flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600/50 transition-all"
                title="Back"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Truck size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-white tracking-tight">
                Load / Route Sheets
              </h1>
              <p className="text-[11px] text-slate-500 -mt-0.5">
                {branding?.company_name || 'GeniReports'} · self-hosted renderer
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setManageOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-600/50 transition-all text-sm"
              title="Manage schedules, recipients, drivers, and view run history"
            >
              <Settings size={14} />
              Manage
            </button>
            <button
              onClick={() => { setSendResult(null); setSendModalOpen(true); }}
              disabled={!isEnabled}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              <Send size={14} />
              Send Now
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Error banner */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 flex items-start gap-3">
            <AlertTriangle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-red-200">{error}</div>
          </div>
        )}

        {/* Disabled-on-tenant warning */}
        {status && !isEnabled && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              Route snapshot is disabled on this tenant. Add
              <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-800/60 text-amber-300 text-xs">
                {'"RouteSnapshot": { "Enabled": true }'}
              </code>
              to <code className="text-xs">appsettings.Production.json</code> on the agent and restart the service.
            </div>
          </div>
        )}

        {/* Snapshot status card */}
        <section className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Clock size={18} className="text-slate-400" />
                Current snapshot
              </h2>
              <p className="text-xs text-slate-400">
                The snapshot is a single-day extract pulled from Advantage. Refresh before rendering to pick up new orders.
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing || !isEnabled}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-500/40 text-blue-200 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh Snapshot'}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Next delivery" value={nextDeliveryDate} />
            <StatCard label="Order lines" value={lastRun ? String(lastRun.ordersRowCount) : '—'} />
            <StatCard label="Closing dates" value={lastRun ? String(lastRun.closingDatesRowCount) : '—'} />
            <StatCard label="Route notes" value={lastRun ? String(lastRun.routeNotesRowCount) : '—'} />
          </div>

          {lastRunAt && (
            <div className="mt-4 text-xs text-slate-500">
              Last refreshed {lastRunAt.toLocaleString()} ({Math.round(lastRun!.durationMs)}ms)
            </div>
          )}
          {!lastRun && isEnabled && (
            <div className="mt-4 text-xs text-amber-300/80">
              No snapshot in memory yet. Click <strong>Refresh Snapshot</strong> to pull from Advantage.
            </div>
          )}
        </section>

        {/* Render controls */}
        <section className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-6">
          <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <FileText size={18} className="text-slate-400" />
            Render reports
          </h2>
          <p className="text-xs text-slate-400 mb-5">
            Each render runs the current snapshot through the RDL templates and returns a PDF.
          </p>

          {/* Driver picker (used by per-driver RouteSheet only) */}
          <div className="mb-5 flex flex-wrap gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                <Users size={12} /> Driver (for per-driver RouteSheet)
              </label>
              <select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                disabled={drivers.length === 0}
                className="w-full sm:w-auto min-w-[160px] px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500/50 disabled:opacity-50"
              >
                {drivers.length === 0 && <option>(no snapshot yet)</option>}
                {drivers.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                <FileText size={12} /> LoadSheet layout
              </label>
              <select
                value={loadGroup}
                onChange={(e) => setLoadGroup(e.target.value)}
                className="w-full sm:w-auto min-w-[200px] px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500/50"
              >
                {LOAD_GROUPS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Render buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <RenderButton
              label="RouteSheet"
              sublabel={selectedDriver ? `Driver ${selectedDriver}` : 'Per-driver detail'}
              busy={rendering === 'RouteSheet'}
              disabled={!isEnabled || !lastRun || !selectedDriver}
              onClick={() => handleRender('RouteSheet')}
            />
            <RenderButton
              label="RouteSheet (All)"
              sublabel="All drivers in one PDF"
              busy={rendering === 'RouteSheet-AllDrivers'}
              disabled={!isEnabled || !lastRun}
              onClick={() => handleRender('RouteSheet-AllDrivers')}
            />
            <RenderButton
              label="LoadSheet"
              sublabel="Item rollup by driver"
              busy={rendering === 'LoadSheet'}
              disabled={!isEnabled || !lastRun}
              onClick={() => handleRender('LoadSheet')}
            />
          </div>
        </section>

        {/* Send Now modal */}
        {sendModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => !sending && setSendModalOpen(false)}
          >
            <div
              className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-700/50 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Send size={18} className="text-emerald-400" /> Send Now
                </h3>
                <button
                  onClick={() => !sending && setSendModalOpen(false)}
                  disabled={sending}
                  className="text-slate-500 hover:text-white transition-colors disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {!sendResult && (
                  <>
                    <p className="text-sm text-slate-300">
                      This refreshes the snapshot, renders the PDFs, and emails them now —
                      same as a scheduled run would do at the configured time.
                    </p>

                    {dailyBatch ? (
                      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                        <div className="text-sm font-medium text-white">Daily Send — all orders incl. 003</div>
                        <div className="text-xs text-slate-300 mt-1">
                          Single daily batch is on: the load sheet shows every unpicked order (truck 003
                          included) and the batch marks them all picked. Emails go to the Second Send
                          recipients plus each driver.
                        </div>
                      </div>
                    ) : (<>
                    <div className="space-y-2">
                      <div className="text-xs uppercase tracking-wide text-slate-400">Send Number</div>
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700/50 bg-slate-900/40 cursor-pointer hover:border-slate-600/70">
                        <input
                          type="radio"
                          checked={sendNumber === 1}
                          onChange={() => setSendNumber(1)}
                          disabled={sending}
                          className="accent-emerald-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white">First Send</div>
                          <div className="text-xs text-slate-400">Group emails to LoadTeam — no per-driver emails</div>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700/50 bg-slate-900/40 cursor-pointer hover:border-slate-600/70">
                        <input
                          type="radio"
                          checked={sendNumber === 2}
                          onChange={() => setSendNumber(2)}
                          disabled={sending}
                          className="accent-emerald-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white">Second Send</div>
                          <div className="text-xs text-slate-400">Group emails to SalesTeam + Management, plus per-driver emails</div>
                        </div>
                      </label>
                    </div>

                    <div className="space-y-2">
                      <div className="text-xs uppercase tracking-wide text-slate-400">Load Sheet Content</div>
                      {([
                        ['all', 'All Orders', 'Every order (the original load sheet). Nothing is marked picked.'],
                        ['unpicked', 'Unpicked Only', 'Only the outstanding quantities to pick; the batch then marks these orders picked.'],
                      ] as [RouteContentMode, string, string][]).map(([value, title, desc]) => (
                        <label key={value} className="flex items-center gap-3 p-3 rounded-lg border border-slate-700/50 bg-slate-900/40 cursor-pointer hover:border-slate-600/70">
                          <input
                            type="radio"
                            checked={sendMode === value}
                            onChange={() => setSendMode(value)}
                            disabled={sending}
                            className="accent-emerald-500"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-white">{title}</div>
                            <div className="text-xs text-slate-400">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                    </>)}
                  </>
                )}

                {sendResult && (
                  <div className="space-y-3">
                    {sendResult.deferred ? (
                      <>
                        {/* Batch accepted: the agent locks + freezes + picks in the
                            background and emails via the callback, so nothing is sent yet.
                            Show a clear "in progress" state instead of a scary "0 sent". */}
                        <div className="flex items-center gap-2 text-sky-300">
                          <CheckCircle2 size={18} />
                          <span className="text-sm font-medium">Batch started — email en route</span>
                        </div>
                        <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-sky-100">
                          The load sheet for <strong>{sendResult.reqDate}</strong> is being prepared:
                          orders are locked and picked in the background, and the email goes out when
                          picking completes (usually ~1–2 minutes). No need to resend.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-emerald-300">
                          <CheckCircle2 size={18} />
                          <span className="text-sm font-medium">Send complete</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <StatCard label="Group recipients" value={String(sendResult.groupRecipientCount)} />
                          <StatCard label="Driver emails" value={String(sendResult.driverEmailCount)} />
                          <StatCard label="Emails sent" value={String(sendResult.emailsSent)} />
                          <StatCard label="Emails failed" value={String(sendResult.emailsFailed)} />
                        </div>
                      </>
                    )}
                    {sendResult.errors?.length > 0 && (
                      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 max-h-32 overflow-auto">
                        <div className="font-medium mb-1">Warnings</div>
                        <ul className="list-disc list-inside space-y-0.5">
                          {sendResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-700/50 flex justify-end gap-2">
                {!sendResult ? (
                  <>
                    <button
                      onClick={() => setSendModalOpen(false)}
                      disabled={sending}
                      className="px-4 py-2 rounded-lg text-slate-300 hover:bg-slate-700/40 transition-all text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendNow}
                      disabled={sending}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50 transition-all text-sm"
                    >
                      {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                      {sending ? 'Sending…' : 'Send'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setSendModalOpen(false)}
                    className="px-4 py-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-white transition-all text-sm"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent downloads (in-memory only — clears on page reload) */}
        {recents.length > 0 && (
          <section className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-6">
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Download size={18} className="text-slate-400" />
              Recent downloads (this session)
            </h2>
            <div className="space-y-1.5">
              {recents.map((r, i) => (
                <div
                  key={`${r.filename}-${i}`}
                  className="flex items-center gap-3 text-sm py-2 px-3 rounded-lg bg-slate-900/40"
                >
                  <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
                  <code className="text-slate-200 truncate flex-1">{r.filename}</code>
                  <span className="text-xs text-slate-500 hidden sm:inline">{r.sizeKB} KB</span>
                  <span className="text-xs text-slate-500">{r.at.toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

// --- Subcomponents -----------------------------------------------------------

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-700/50 bg-slate-900/40 px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function RenderButton({
  label, sublabel, busy, disabled, onClick,
}: {
  label: string;
  sublabel: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      className="text-left p-4 rounded-xl border border-slate-700/50 bg-slate-900/40 hover:bg-slate-900/70 hover:border-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all group"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-white font-medium">{label}</div>
        {busy
          ? <RefreshCw size={16} className="animate-spin text-blue-400" />
          : <Download size={16} className="text-slate-500 group-hover:text-slate-300 transition-colors" />}
      </div>
      <div className="text-xs text-slate-400">{sublabel}</div>
    </button>
  );
}

// --- Utils -------------------------------------------------------------------

function formatError(e: unknown): string {
  if (e && typeof e === 'object') {
    type AxiosLike = { response?: { data?: { error?: string } }; message?: string };
    const ax = e as AxiosLike;
    return ax.response?.data?.error || ax.message || 'Unknown error';
  }
  return String(e);
}
